// Leads (prospectos) board data for the tenant workspace. A kanban
// funnel: each lead sits in a stage and moves right as it progresses. The
// prospect detail (view 2 — stage strip, timeline, agendar cita) is a follow-up.
//
// Wired: getLeadsBoard()/getLead() -> GET /leads + /leads/:id;
// moveLead() -> PATCH /leads/:id { stageId }, wired into the board's drag handler
// so stage moves persist (optimistic on drop, reverted on error).

import { isAxiosError } from 'axios'

import { apiClient } from '../../api/client.js'

export type SpecialtyTone = 'sens' | 'info' | 'warn' | 'ok' | 'draft'
export type LeadMetaKind = 'time' | 'date' | 'done'

/** What a stage MEANS. Sent by the API so the board never has to recognise a
 *  stage by name — stage names are the tenant's own words. */
export type StageKind = 'open' | 'won' | 'lost'

/** Quién se hace responsable de un prospecto al entrar a la etapa. Mismo
 *  vocabulario que Configuración → Procesos: la etapa se configura ahí y el
 *  tablero solo lo reporta. */
export type StageOwnerType = 'bot' | 'team' | 'member'
export type StageTeamPolicy = 'balanced' | 'none'

export interface LeadStage {
  id: string
  name: string
  /** Dot colour (theme-independent, like the kanban accents). */
  dot: string
  kind: StageKind
  ownerType: StageOwnerType
  /** Ya resuelto por el servidor. Ausente cuando la etapa no es de una persona,
   *  y cuando quien la atendía salió del equipo. */
  ownerName?: string
  /** `null` significa lo mismo que 'none': sin asignación automática. */
  teamPolicy: StageTeamPolicy | null
  /** Nombre de la política nombrada de la etapa, ya resuelto por el servidor.
   *  Ausente cuando la etapa no usa una. */
  teamPolicyName?: string
  /** La etapa de cita agendada del proceso. Los prospectos que ya están aquí no
   *  cuentan como "necesitan seguimiento". */
  isCommitment: boolean
}

/** The funnel the board is showing. `null` only for a member with no tenant. */
export interface LeadsPipeline {
  id: string
  key: string
  name: string
}

/** One entry of the funnel selector. The tenant can own several since PR 17;
 *  a tenant with exactly one never sees the control (solo-mode rule). */
export interface LeadsPipelineOption {
  id: string
  key: string
  name: string
  isDefault: boolean
}

export interface Lead {
  id: string
  name: string
  stageId: string
  specialty: { label: string; tone: SpecialtyTone }
  meta: string
  metaKind: LeadMetaKind
  /** Sin actividad en 3 días o más, en una etapa abierta que no es la de cita.
   *  Lo decide el servidor con la MISMA regla que manda la notificación
   *  nocturna; el chip "Necesitan seguimiento" filtra por aquí. */
  stale: boolean
  /** "4 días sin actividad" — solo viene cuando `stale`, y reemplaza la línea
   *  de meta en la tarjeta. */
  staleText?: string
  assignee?: string
  /** Reached a terminal "won" stage. El borde de la tarjeta se pinta desde el
   *  `kind` de su columna (ganada en verde, perdida en rojo, por la misma
   *  regla); esto es lo mismo dicho por prospecto. */
  won?: boolean
  /** Answers the bot captured + remembered (booking specialty/date, budget…),
   *  display-ready. Empty/absent when nothing was captured. */
  captured?: { label: string; value: string }[]
}

/** El tipo de dato de un campo del catálogo. Mismo vocabulario que la ficha de
 *  Procesos; se declara aquí para no arrastrar esa página. */
export type LeadFieldDataType = 'text' | 'number' | 'date' | 'boolean' | 'enum' | 'phone' | 'email'

/** Un campo de la ficha del proceso, listo para dibujar su
 *  control. `interes` (el interés del giro: Especialidad, Modelo de interés,
 *  Artículo) viene primero y vive en una columna, no en la bolsa: eso lo
 *  resuelve el servidor, aquí es un campo más. */
export interface LeadFieldDef {
  key: string
  label: string
  dataType: LeadFieldDataType
  options: string[] | null
}

export interface LeadsBoard {
  pipeline: LeadsPipeline | null
  /** Every funnel the tenant owns — what the selector renders from. */
  pipelines: LeadsPipelineOption[]
  stages: LeadStage[]
  leads: Lead[]
  /** What the filters may offer, computed by the server INSIDE the caller's
   *  visibility scope. `areas` is empty when this funnel has no área on any
   *  card, and the filter then doesn't render at all. */
  facets: { areas: string[] }
  /** La ficha de ESTE proceso: lo que el modal de alta pide y lo que la ficha
   *  del prospecto edita. Vacío si el proceso no lleva ningún dato. */
  fields: LeadFieldDef[]
}

/** One tick of the "Asignado a" filter: a member's id, or one of the two tokens
 *  the server resolves itself — `me` (the caller, read from the token, so the
 *  web never needs to know its own user id) and `none` (the cards nobody owns). */
export type LeadsAssigneeToken = string

/** What the board is asking for. Every one of these is a SERVER filter: the
 *  board is capped at 300 cards, so narrowing them in the browser would hide
 *  cards instead of finding them. */
export interface LeadsBoardFilters {
  /** Any of these people (OR). Empty = everybody. */
  assignees?: LeadsAssigneeToken[]
  /** Any of these áreas (OR), matched against the facet values. Empty = all. */
  areas?: string[]
  /** WHICH funnel; the tenant's default when omitted. */
  pipelineId?: string
}

/** GET /leads — the kanban funnel: stage definitions, every lead with its stage,
 *  and the filter facets. The two filter groups compose as AND (those people,
 *  and one of those áreas) and both narrow inside the tenant's visibility
 *  policy — they can never widen it. */
export async function getLeadsBoard(filters: LeadsBoardFilters = {}): Promise<LeadsBoard> {
  const { assignees = [], areas = [], pipelineId } = filters
  const params = {
    ...(assignees.length > 0 ? { assignees: assignees.join(',') } : {}),
    ...(areas.length > 0 ? { areas: areas.join(',') } : {}),
    ...(pipelineId ? { pipeline_id: pipelineId } : {}),
  }
  const res = await apiClient.get<{ data: LeadsBoard }>('/leads', {
    ...(Object.keys(params).length > 0 ? { params } : {}),
  })
  return res.data.data
}

/** PATCH /leads/:id { stageId } — persists a kanban drop (board applies it optimistically, reverts on error). */
export async function moveLead(id: string, stageId: string): Promise<void> {
  await apiClient.patch(`/leads/${id}`, { stageId })
}

export interface NewLead {
  name: string
  /** Los datos del catálogo que capturó el modal, por clave. `interes` incluido:
   *  el servidor lo enruta a la columna del área. Las claves vacías no se
   *  mandan. */
  fields?: Record<string, string>
  origin?: string
  /** Hand the prospect to somebody the moment it's created. */
  assignee_id?: string
  /** Which funnel it lands in. The board sends the one it is showing, so a card
   *  typed in while looking at a funnel does not appear in a different one. */
  pipelineId?: string
}

/** Create a prospect manually; returns the new lead id. */
export async function createLead(input: NewLead): Promise<string> {
  const res = await apiClient.post<{ data: { id: string } }>('/leads', input)
  return res.data.data.id
}

// --- Prospect detail (mockup 23, view 2) --------------------------------------

export type ActivityKind = 'created' | 'bot' | 'assigned' | 'note' | 'stage'
export interface LeadActivity {
  id: string
  kind: ActivityKind
  text: string
  time: string
  note?: string
}

export interface LeadDetail extends Lead {
  /** The latest conversation with this prospect's contact, when there is one. */
  conversationId?: string | null
  /** Who owns the card. `assignee` on the card is only their initials; the
   *  Asignar control needs the id to preselect. */
  assigneeId?: string | null
  /** The contact this prospect is, when one is linked — the way back to the
   *  person's own record. `null` when nothing was linked. */
  contact?: { id: string; name: string } | null
  phone: string
  /** A5 — CÓMO LLEGÓ: derivado en el servidor de las identidades de canal del
   *  contacto vinculado ("WhatsApp", "Instagram", "Messenger"), o "Alta manual"
   *  cuando no hay ninguna. Reemplaza a `channel`, que salía de una columna con
   *  DEFAULT 'whatsapp' que ningún alta escribe. */
  arrivedBy: string
  /** A5 — QUÉ LO TRAJO: atribución libre (recomendación, campaña), `null`
   *  cuando nadie la escribió. La ficha oculta el renglón en ese caso. */
  origin: string | null
  created: string
  stages: LeadStage[]
  activity: LeadActivity[]
  /** El PROCESO en el que vive la tarjeta. La ficha lo dice junto a la etapa
   *  (una etapa solo significa algo dentro de su embudo) y lo usa para el
   *  regreso: la ficha es una ruta aparte, así que sin esto no hay forma de
   *  volver al tablero del que se abrió. */
  pipeline?: { id: string; name: string } | null
  /** El nombre REAL de la etapa de cita agendada de este proceso, o `null` si el
   *  proceso no marca ninguna. El control de cita se lee de aquí: prometer
   *  "Cita agendada" a secas mentía en cuanto alguien renombraba la etapa, y
   *  mentía siempre en un proceso con "Ninguna". */
  commitmentStageName: string | null
  /** La fecha guardada, ya formateada ("24 de junio, 15:00"), o `null`. */
  appointmentAt: string | null
  /** Los datos de este proceso — la sección Datos de la ficha. */
  fields: LeadFieldDef[]
  /** El valor actual de cada clave del catálogo ('' cuando no hay nada). */
  fieldValues: Record<string, string>
  /** Respuestas guardadas cuya clave el catálogo ya no conoce. Se muestran de
   *  solo lectura: un dato capturado no se pierde porque el campo se renombró,
   *  y tampoco se invita a editar una clave que nadie define. */
  uncatalogued: { label: string; value: string }[]
}

/** GET /leads/:id — prospect detail with activity timeline; null on 404, rethrows other errors. */
export async function getLead(id: string): Promise<LeadDetail | null> {
  try {
    const res = await apiClient.get<{ data: LeadDetail }>(`/leads/${id}`)
    return res.data.data
  } catch (err) {
    if (isAxiosError(err) && err.response?.status === 404) return null
    throw err
  }
}

/** PUT /leads/:id/appointment — guarda la fecha (ISO) y adelanta el prospecto a
 *  la etapa de cita agendada DEL PROCESO, la que el espacio haya marcado y como
 *  se llame. Un proceso que no marca ninguna guarda la fecha y deja la tarjeta
 *  donde está: por eso la ficha lee `commitmentStageName` antes de prometer
 *  nada. (Antes decía "avanza a 'Cita agendada'", un nombre inventado aquí.) */
export async function scheduleAppointment(id: string, appointmentAt: string): Promise<void> {
  await apiClient.put(`/leads/${id}/appointment`, { appointmentAt })
}

/** POST /leads/:id/assign — hand the prospect to a member, or `null` to leave
 *  it unassigned. */
export async function assignLead(id: string, memberId: string | null): Promise<void> {
  await apiClient.post(`/leads/${encodeURIComponent(id)}/assign`, { member_id: memberId })
}

/** Append a team note to the lead. */
export async function addLeadNote(id: string, body: string): Promise<void> {
  await apiClient.post(`/leads/${id}/notes`, { body })
}

/** PATCH /leads/:id { fields } — guarda los datos del catálogo del prospecto
 *  (F26). Una clave en blanco borra la respuesta; `interes` va a la columna del
 *  área. El servidor rechaza claves que no estén en el catálogo activo. */
export async function updateLeadFields(id: string, fields: Record<string, string>): Promise<void> {
  await apiClient.patch(`/leads/${encodeURIComponent(id)}`, { fields })
}

/** PATCH /leads/:id { contact_id } — tie the prospect to one of the tenant's
 *  contacts (F24: a lead the Agente never linked can get its person by hand). */
export async function linkLeadContact(id: string, contactId: string): Promise<void> {
  await apiClient.patch(`/leads/${encodeURIComponent(id)}`, { contact_id: contactId })
}
